import { z } from 'zod'

import { RegistryAsset } from '@scalar-org/entities/managed-doc'
import { docVersionSchema, namespaceSchema } from '@scalar-org/entities/utility'

function versionFormat(version: string | undefined) {
  if (!version || version === 'latest') return 'latest'
  return docVersionSchema.parse(version)
}

type RegistryFileTypes = 'json' | 'yaml' | 'zip'

/** Registry GCS filetype (YAML, JSON) */
function filetypeFormat(filetype: RegistryFileTypes | undefined) {
  return filetype ? z.enum(['yaml', 'json', 'zip']).parse(filetype) : 'yaml'
}

export type RegistryFormat = 'json' | 'yaml' | 'preview'

/** Registry content format option (YAML, JSON, Preview Mode) */
function registryFormat(format: RegistryFormat) {
  return z.enum(['yaml', 'json', 'preview']).parse(format)
}

function buildAsset(namespace: string, asset: RegistryAsset, slug: string) {
  return {
    /**
     * Get registry record path.
     *
     * Registry collection records have a base resource path
     * with the primary registry item metadata: namespace,
     * asset type and slug.
     *
     * @example scalar/apis/scalar-galaxy
     */
    path: () => {
      return `${namespace}/${asset}/${slug}`
    },
    /**
     * Get registry GCS file storage path
     *
     * Unlike registry record paths, storage paths contain the version
     * of a registry item, and the respective file type.
     *
     * @example scalar/apis/scalar-galaxy/1.0.0.json
     */
    file: (opts?: {
      version?: string
      build?: string
      upgraded?: boolean
      type?: 'json' | 'yaml' | 'zip'
    }) => {
      return `${namespace}/${asset}/${slug}/${opts?.build || versionFormat(opts?.version)}${opts?.upgraded ? '_upgraded' : ''}.${filetypeFormat(opts?.type)}`
    },
    /**
     * Get registry GCS folder storage path
     *
     * @example scalar/apis/scalar-galaxy
     */
    folder: (opts?: { version?: string }) => {
      return `${namespace}/${asset}/${slug}${opts?.version ? `/${opts.version}` : ''}`
    },
    /**
     * Get a full standardized registry url path
     *
     * Our registry resolver understands a few path formats.
     * However, this is our primary registry format to use across
     * internal products.
     *
     * @example @scalar/apis/scalar-galaxy/1.0.0?format=preview
     */
    url: (opts?: { version?: string; format?: RegistryFormat }) => {
      return `@${namespace}/${asset}/${slug}@${versionFormat(opts?.version)}${opts?.format ? `?format=${registryFormat(opts.format)}` : ''}`
    },
  }
}

export function registry(teamNamespace: string) {
  const namespace = namespaceSchema.parse(teamNamespace)

  return {
    apis: (slug: string) => buildAsset(namespace, RegistryAsset.Apis, slug),
    rules: (slug: string) => buildAsset(namespace, RegistryAsset.Rules, slug),
    sdks: (slug: string) => buildAsset(namespace, RegistryAsset.Sdks, slug),
    schemas: (slug: string) =>
      buildAsset(namespace, RegistryAsset.Schemas, slug),
    workspaces: (slug: string) =>
      buildAsset(namespace, RegistryAsset.Workspaces, slug),
  }
}
